import { NodeRoles, Node, NodeResponse } from '@cast/types';

import { isNonEmptyArray } from 'utils/types/non-empty-array';

import {
  AUTOSCALING_REMOVAL_DISABLED_LABEL,
  KARPENTER_NODEPOOL_LABEL_KEY,
  TEST_NODE_LABEL_KEY,
} from '../constants/node-labels';
import {
  BinPackingBlockedDetails,
  NodeWithDependencies,
  WorkloadBaseRunningInNode,
} from '../types';

const nodeTypeMap: Record<NodeRoles, string> = {
  [NodeRoles.master]: 'Control plane',
  [NodeRoles.worker]: 'Node',
  [NodeRoles.NODE_TYPE_INVALID]: '',
  [NodeRoles.NODE_TYPE_MASTER]: '',
  [NodeRoles.NODE_TYPE_WORKER]: '',
};

export const transformNodeRole = (value: string) => {
  if (value in nodeTypeMap) {
    return nodeTypeMap[value as NodeRoles];
  }
  return value;
};

export const isRemovalDisabled = (node: Node) => {
  return node.labels?.[AUTOSCALING_REMOVAL_DISABLED_LABEL] === 'true';
};

export const isTestNode = (node: Node): boolean =>
  node.labels?.[TEST_NODE_LABEL_KEY] === 'true';

export const isManagedByCASTAI = (node: NodeResponse) => {
  return node.labels?.['provisioner.cast.ai/managed-by'] === 'cast.ai';
};

export const getNodeSelectionTemplateName = (node: Node) =>
  node.labels?.['scheduling.cast.ai/node-template'];

export const getNodeSelectionTemplateVersion = (node: Node) =>
  node.labels?.['scheduling.cast.ai/node-template-version'];

export const getNodeConfigurationName = (node: Node) =>
  node.labels?.['provisioner.cast.ai/node-configuration-name'];

export const getNodeConfigurationVersion = (node: Node) =>
  node.labels?.['provisioner.cast.ai/node-configuration-version'];

export const getNodePoolName = (node: Node) =>
  node.labels?.[KARPENTER_NODEPOOL_LABEL_KEY];

export const getNodeClassName = (node: Node) =>
  node.labels?.['karpenter.k8s.aws/ec2nodeclass'];

export const getMasterNodes = (nodes: Node[]) =>
  nodes.filter((node: Node) => node?.role === NodeRoles.master);

const filterProblematicWorkloads = (
  node: NodeWithDependencies
): WorkloadBaseRunningInNode[] => {
  return (node.workloads || []).filter((workload) => !!workload.issues.length);
};

type BinPackingBlockedResult =
  | {
      isBinPackingBlocked: false;
    }
  | {
      isBinPackingBlocked: true;
      details: BinPackingBlockedDetails;
    };

export const checkBinPackingBlock = (
  node: NodeWithDependencies
): BinPackingBlockedResult => {
  const problematicWorkloads = filterProblematicWorkloads(node);
  const hasProblematicWorkloads = isNonEmptyArray(problematicWorkloads);
  const nodeProblems = node.problems || [];
  const hasNodeProblems = isNonEmptyArray(nodeProblems);

  if (hasNodeProblems && hasProblematicWorkloads) {
    return {
      isBinPackingBlocked: true,
      details: {
        reason: 'node-and-workloads-problems',
        nodeProblems,
        problematicWorkloads,
      },
    };
  }
  if (hasProblematicWorkloads) {
    return {
      isBinPackingBlocked: true,
      details: {
        reason: 'workloads-problems',
        problematicWorkloads,
      },
    };
  }

  if (hasNodeProblems) {
    return {
      isBinPackingBlocked: true,
      details: {
        reason: 'node-problems',
        nodeProblems,
      },
    };
  }

  return {
    isBinPackingBlocked: false,
  };
};
